/**
 * Breadcrumb navigation context.
 *
 * Follow-route mode: nav anchors carry data-nav-key. On click the key is
 * remembered in localStorage against the destination pathname, never in the
 * URL, so every crawlable and shareable URL stays clean (AAS-5716). The
 * destination page looks itself up on load. localStorage (not session) so a
 * cmd/middle-click into a new tab still finds its entry; the map is keyed by
 * pathname so N tabs opened from one PLP cannot overwrite each other; entries
 * expire after BREADCRUMB_NAV_TTL and the map is capped at BREADCRUMB_NAV_MAX.
 *
 * On PLPs the effective key (the one the pre-paint swap applied, else the
 * PLP's own default node) is forwarded onto product-card clicks via a
 * delegated listener — facets.js replaces #ProductGridContainer innerHTML on
 * every filter change, so per-card handling would not survive.
 *
 * The visible-trail swap itself is NOT here — it is a synchronous inline
 * script in breadcrumb-engine.liquid so it runs pre-paint. That script reads
 * the same storage key and must stay in sync with the shape written here.
 */
(() => {
  const mode = document.currentScript?.dataset.mode || 'follow_route';
  const followRoute = mode !== 'always_default';

  const STORAGE_KEY = 'breadcrumb-nav';
  const BREADCRUMB_NAV_TTL = 15 * 60 * 1000;
  const BREADCRUMB_NAV_MAX = 20;

  // { "<pathname>": { k: "<nav-key>", t: <written-at ms> } }
  const rememberNav = (anchor, key) => {
    if (!key) return;
    try {
      const url = new URL(anchor.href, location.origin);
      if (url.origin !== location.origin) return;
      const now = Date.now();
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      const entries = Object.entries(stored).filter(([, entry]) => now - entry.t < BREADCRUMB_NAV_TTL);
      entries.push([url.pathname, { k: key, t: now }]);
      entries.sort((a, b) => b[1].t - a[1].t);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries.slice(0, BREADCRUMB_NAV_MAX))));
    } catch {
      /* unparseable href (e.g. "#") or storage unavailable — default trail */
    }
  };

  const onNavigate = (handler) => {
    ['click', 'auxclick'].forEach((type) => {
      document.addEventListener(type, handler, true);
    });
  };

  if (followRoute) {
    onNavigate((event) => {
      if (!event.target || !event.target.closest) return;

      const navLink = event.target.closest('a[data-nav-key]');
      if (navLink) {
        rememberNav(navLink, navLink.dataset.navKey);
        return;
      }

      // Mobile drawer "shop all" header link: its href is set by the drawer JS
      // from the active panel; the panel carries the nav key.
      const shopAll = event.target.closest('a.nav-drawer__shop-all');
      if (shopAll) {
        const panel = document.querySelector('.nav-drawer__panel.is-active[data-nav-key]');
        if (panel) rememberNav(shopAll, panel.dataset.navKey);
      }
    });
  }

  const plpNav = document.querySelector('nav[data-breadcrumb-context="collection"]');
  if (plpNav && followRoute) {
    // data-active-nav-key is set by the pre-paint swap in breadcrumb-engine
    // once it has validated the stored key against this page's trails.
    const effectiveKey = plpNav.dataset.activeNavKey || plpNav.dataset.defaultNavKey || null;

    if (effectiveKey) {
      onNavigate((event) => {
        if (!event.target || !event.target.closest) return;
        const productLink = event.target.closest('#ProductGridContainer a[href*="/products/"]');
        if (productLink) rememberNav(productLink, effectiveKey);
      });
    }
  }

  const updateScrollPosition = (nav, list) => {
    const maxScroll = list.scrollWidth - list.clientWidth;

    if (maxScroll <= 1) {
      delete nav.dataset.scrollPosition;
      return;
    }

    if (list.scrollLeft <= 1) {
      nav.dataset.scrollPosition = 'start';
    } else if (list.scrollLeft >= maxScroll - 1) {
      nav.dataset.scrollPosition = 'end';
    } else {
      nav.dataset.scrollPosition = 'middle';
    }
  };

  const mobileBreadcrumbQuery = window.matchMedia('(max-width: 1023px)');
  const initializedBreadcrumbs = new WeakSet();

  const initBreadcrumbScroll = (nav) => {
    if (initializedBreadcrumbs.has(nav)) return;

    const list = nav.querySelector('.breadcrumb__list');
    if (!list) return;
    initializedBreadcrumbs.add(nav);

    let frameId = null;
    let scrollToEnd = mobileBreadcrumbQuery.matches;

    const scheduleUpdate = () => {
      if (frameId !== null) return;

      frameId = requestAnimationFrame(() => {
        frameId = null;
        if (scrollToEnd) {
          list.scrollLeft = list.scrollWidth;
          scrollToEnd = false;
        }
        updateScrollPosition(nav, list);
      });
    };

    // Layout/font growth must keep an end-anchored trail pinned to the end.
    const preserveEndAnchor = () => {
      if (nav.dataset.scrollPosition === 'end') scrollToEnd = true;
      scheduleUpdate();
    };

    list.addEventListener('scroll', scheduleUpdate, { passive: true });

    mobileBreadcrumbQuery.addEventListener('change', (event) => {
      if (event.matches) {
        scrollToEnd = true;
      } else {
        scrollToEnd = false;
        list.scrollLeft = 0;
      }
      scheduleUpdate();
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(preserveEndAnchor);
      resizeObserver.observe(list);
      list.querySelectorAll('li').forEach((item) => resizeObserver.observe(item));
    } else {
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    document.fonts?.ready.then(preserveEndAnchor);
    scheduleUpdate();
  };

  const initBreadcrumbScrolls = (root = document) => {
    root.querySelectorAll('nav[data-breadcrumb-context]').forEach(initBreadcrumbScroll);
  };

  initBreadcrumbScrolls();
  document.addEventListener('shopify:section:load', (event) => {
    initBreadcrumbScrolls(event.target);
  });
})();
